/* <cart-page> — progressive enhancement for the full cart page
   (sections/main-cart.liquid). Without JS the page is a plain form that
   POSTs updates[] to /cart. With JS, qty changes and removes go through
   CartAPI (the single cart path) and the section re-renders itself via
   the Section Rendering API. Lazy-loaded via data-lazy-module. */

import { bus, fetchSections } from './critical.js';
import { CartAPI, setGiftFlag } from './cart.js';

class CartPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    if (!this.sectionId) return;
    /* Hide the no-JS "Update cart" button — changes apply instantly now. */
    for (const el of this.querySelectorAll('[data-cart-update-fallback]')) el.hidden = true;
    this.wire();
    /* Re-render after any cart mutation (including drawer upsell adds). */
    this.unsub = bus.on('cart:updated', () => this.render());
  }

  disconnectedCallback() {
    this.unsub?.();
  }

  wire() {
    for (const button of this.querySelectorAll('[data-cart-remove]')) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (button.dataset.giftLine) setGiftFlag('dismissed', button.dataset.giftLine); /* #277: the shopper said no */
        this.mutating(() => CartAPI.change(button.dataset.cartRemove, 0));
      });
    }
    /* #90 pill: -/+ buttons carry the ABSOLUTE target qty in data-qty
       (server-computed), so repeated clicks are idempotent. */
    for (const button of this.querySelectorAll('button[data-cart-qty][data-qty]')) {
      button.addEventListener('click', () => {
        this.mutating(() => CartAPI.change(button.dataset.cartQty, parseInt(button.dataset.qty, 10)));
      });
    }
    for (const input of this.querySelectorAll('input[data-cart-qty]')) {
      input.addEventListener('change', () => {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.mutating(() => CartAPI.change(input.dataset.cartQty, quantity));
      });
    }
  }

  async render() {
    /* A cart:updated during an in-flight render queues ONE re-render so the
       newest cart state always lands (never silently dropped). */
    if (this.rendering) {
      this.pendingRender = true;
      return;
    }
    this.rendering = true;
    try {
      const sections = await fetchSections(this.sectionId);
      const html = sections?.[this.sectionId];
      if (!html) return;
      const incoming = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-cart-page-inner]');
      const current = this.querySelector('[data-cart-page-inner]');
      if (incoming && current) {
        /* #90 focus continuity (drawer parity): the swap drops focus to
           <body>; land it back on the same pill control. hadFocus guards
           against stealing focus from the drawer when both re-render. */
        const focused = document.activeElement;
        const hadFocus = this.contains(focused);
        current.replaceWith(incoming);
        for (const el of this.querySelectorAll('[data-cart-update-fallback]')) el.hidden = true;
        this.wire();
        if (hadFocus) {
          const { cartQty, step } = focused.dataset || {};
          if (cartQty) this.querySelector(`[data-cart-qty="${cartQty}"]${step ? `[data-step="${step}"]` : ''}`)?.focus();
        }
      }
    } catch (error) {
      /* DOM keeps the last rendered state; say the refresh failed. */
      this.querySelector('[data-cart-error]')?.replaceChildren(error.message);
    } finally {
      this.rendering = false;
      if (this.pendingRender) {
        this.pendingRender = false;
        this.render();
      }
    }
  }

  async mutating(fn) {
    this.setAttribute('aria-busy', 'true');
    try {
      await fn();
    } catch (error) {
      this.querySelector('[data-cart-error]')?.replaceChildren(error.message);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
